import { confirmDialog } from '../ui/ConfirmDialog';
import React, { useState, useEffect, useCallback } from 'react';
import { clsx } from 'clsx';
import { StickyNote, Plus, Trash2, Pencil, Loader2, Check, X, Lock, FileText } from 'lucide-react';
import { supabase } from '../../lib/supabase';

// Dated property notes — list + add/edit/delete for the current property.
// Backed by:
//   - GET list:    SELECT FROM property_notes (RLS: agency-scoped read)
//   - add/update/delete: SECURITY DEFINER RPCs add/update/delete_property_note
//     (migration 20260609145716_property_notes.sql) which enforce
//     "assigned agent OR agency_admin (same agency) OR super_admin" + the
//     20-notes-per-property cap.
//
// Two note types:
//   - 'private'      → internal only (this section, detail view)
//   - 'owner_report' → also rendered on the Reporte Cliente
// In the report this component is reused read-only with onlyType='owner_report'.
//
// The caller gates write affordances: canWrite (add), currentUserId +
// canManageOthers (per-note edit/delete = author, or admin for any note).

const MAX_NOTES = 20;
const REMARK_MAX = 2000;

interface PropertyNote {
  id: string;
  note_type: 'private' | 'owner_report';
  remark: string;
  note_at: string;
  created_by: string | null;
  author?: { full_name: string | null } | null;
}

interface Props {
  propertyId: string;
  theme: 'light' | 'dark' | 'high-contrast';
  canWrite?: boolean;
  currentUserId?: string | null;
  canManageOthers?: boolean;
  readOnly?: boolean;
  onlyType?: 'private' | 'owner_report';
  title?: string;
}

const pad = (n: number) => String(n).padStart(2, '0');
function toLocalInput(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fmtDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('es-GT', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

const TypeBadge: React.FC<{ t: 'private' | 'owner_report'; isLight: boolean }> = ({ t, isLight }) => (
  <span className={clsx(
    'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider',
    t === 'owner_report'
      ? (isLight ? 'bg-indigo-100 text-indigo-700' : 'bg-indigo-500/20 text-indigo-300')
      : (isLight ? 'bg-amber-100 text-amber-700' : 'bg-amber-500/20 text-amber-300'),
  )}>
    {t === 'owner_report' ? <><FileText size={11} /> En reporte</> : <><Lock size={11} /> Privada</>}
  </span>
);

// Shared type-toggle + datetime + remark editor (used by add and inline edit).
// Must live at module scope: declaring it inside the parent's render gives it a
// new component identity every render, remounting the textarea on each keystroke.
const Editor: React.FC<{
  remark: string; setRemark: (v: string) => void;
  type: 'private' | 'owner_report'; setType: (v: 'private' | 'owner_report') => void;
  at: string; setAt: (v: string) => void;
  onSave: () => void; onCancel: () => void; saveLabel: string;
  saving: boolean; isLight: boolean;
}> = ({ remark, setRemark, type, setType, at, setAt, onSave, onCancel, saveLabel, saving, isLight }) => (
  <div className={clsx('space-y-3 p-3 rounded-xl border', isLight ? 'bg-slate-50 border-slate-200' : 'bg-white/5 border-white/10')}>
    <textarea
      value={remark}
      onChange={e => setRemark(e.target.value.slice(0, REMARK_MAX))}
      placeholder="Escribe la nota…"
      rows={3}
      className={clsx(
        'w-full rounded-lg border px-3 py-2 text-sm resize-y focus:ring-2 focus:ring-indigo-500/40 focus:outline-none',
        isLight ? 'bg-white border-slate-200 text-slate-900 placeholder:text-slate-400' : 'bg-white/5 border-white/10 text-white placeholder:text-slate-500',
      )}
    />
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-1 p-1 rounded-lg border border-slate-200 dark:border-white/10">
        {(['private', 'owner_report'] as const).map(t => (
          <button
            key={t}
            type="button"
            onClick={() => setType(t)}
            className={clsx(
              'px-2.5 py-1 rounded-md text-[11px] font-bold transition-colors',
              type === t
                ? (isLight ? 'bg-indigo-600 text-white' : 'bg-indigo-500 text-white')
                : (isLight ? 'text-slate-600 hover:bg-slate-100' : 'text-slate-300 hover:bg-white/10'),
            )}
          >
            {t === 'owner_report' ? 'En reporte' : 'Privada'}
          </button>
        ))}
      </div>
      <input
        type="datetime-local"
        value={at}
        onChange={e => setAt(e.target.value)}
        className={clsx(
          'rounded-lg border px-2.5 py-1.5 text-xs',
          isLight ? 'bg-white border-slate-200 text-slate-700' : 'bg-white/5 border-white/10 text-slate-200',
        )}
      />
      <div className="flex items-center gap-2 ml-auto">
        <button
          type="button"
          onClick={onCancel}
          className={clsx('flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold', isLight ? 'text-slate-600 hover:bg-slate-100' : 'text-slate-300 hover:bg-white/10')}
        >
          <X size={14} /> Cancelar
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving || !remark.trim()}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} {saveLabel}
        </button>
      </div>
    </div>
  </div>
);

export const PropertyNotesSection: React.FC<Props> = ({
  propertyId,
  theme,
  canWrite = false,
  currentUserId = null,
  canManageOthers = false,
  readOnly = false,
  onlyType,
  title,
}) => {
  const isLight = theme !== 'dark';
  const panelCls = clsx('p-6 rounded-2xl border', isLight ? 'bg-white border-slate-100 shadow-sm' : 'glass-panel border-white/5');

  const [notes, setNotes] = useState<PropertyNote[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Add form
  const [adding, setAdding] = useState(false);
  const [addRemark, setAddRemark] = useState('');
  const [addType, setAddType] = useState<'private' | 'owner_report'>('private');
  const [addAt, setAddAt] = useState(() => toLocalInput(new Date()));

  // Inline edit
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editRemark, setEditRemark] = useState('');
  const [editType, setEditType] = useState<'private' | 'owner_report'>('private');
  const [editAt, setEditAt] = useState('');

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    let q = supabase
      .from('property_notes')
      .select('id, note_type, remark, note_at, created_by, author:profiles!property_notes_created_by_fkey(full_name)')
      .eq('property_id', propertyId)
      .eq('is_deleted', false);
    if (onlyType) q = q.eq('note_type', onlyType);
    const { data, error: err } = await q.order('note_at', { ascending: false });
    if (err) {
      setError(err.message);
      setNotes([]);
    } else {
      setNotes((data ?? []) as unknown as PropertyNote[]);
      setError(null);
    }
    setLoading(false);
  }, [propertyId, onlyType]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const total = notes?.length ?? 0;
  const atCap = total >= MAX_NOTES;

  const handleAdd = async () => {
    const remark = addRemark.trim();
    if (!remark) return;
    setSaving(true);
    setError(null);
    const { error: err } = await supabase.rpc('add_property_note', {
      p_property_id: propertyId,
      p_remark: remark,
      p_note_type: addType,
      p_note_at: new Date(addAt).toISOString(),
    });
    if (err) {
      setError(err.message);
    } else {
      setAddRemark('');
      setAddType('private');
      setAddAt(toLocalInput(new Date()));
      setAdding(false);
      await fetchNotes();
    }
    setSaving(false);
  };

  const startEdit = (n: PropertyNote) => {
    setEditingId(n.id);
    setEditRemark(n.remark);
    setEditType(n.note_type);
    setEditAt(toLocalInput(new Date(n.note_at)));
  };

  const handleUpdate = async () => {
    const remark = editRemark.trim();
    if (!editingId || !remark) return;
    setSaving(true);
    setError(null);
    const { error: err } = await supabase.rpc('update_property_note', {
      p_note_id: editingId,
      p_remark: remark,
      p_note_type: editType,
      p_note_at: new Date(editAt).toISOString(),
    });
    if (err) {
      setError(err.message);
    } else {
      setEditingId(null);
      await fetchNotes();
    }
    setSaving(false);
  };

  const handleDelete = async (n: PropertyNote) => {
    if (!await confirmDialog('¿Eliminar esta nota? No se puede deshacer.')) return;
    setDeletingId(n.id);
    setError(null);
    const { error: err } = await supabase.rpc('delete_property_note', { p_note_id: n.id });
    if (err) {
      setError(err.message);
    } else {
      setNotes(prev => (prev ?? []).filter(x => x.id !== n.id));
    }
    setDeletingId(null);
  };

  const canEditNote = (n: PropertyNote) => !readOnly && (canManageOthers || (!!currentUserId && n.created_by === currentUserId));

  return (
    <div className={panelCls}>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h3 className={clsx('flex items-center gap-2 text-sm font-black uppercase tracking-widest', isLight ? 'text-slate-700' : 'text-white')}>
          <StickyNote size={16} /> {title ?? 'Bitácora de notas'}
        </h3>
        {!readOnly && canWrite && (
          <div className="flex items-center gap-3">
            <span className={clsx('text-[11px] font-bold', isLight ? 'text-slate-400' : 'text-slate-500')}>{total}/{MAX_NOTES}</span>
            {!adding && (
              <button
                onClick={() => { setAdding(true); setAddAt(toLocalInput(new Date())); }}
                disabled={atCap}
                title={atCap ? `Máximo ${MAX_NOTES} notas por propiedad` : undefined}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus size={14} /> Agregar nota
              </button>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className={clsx('mb-3 p-2.5 rounded-lg text-xs font-medium', isLight ? 'bg-rose-50 text-rose-700 border border-rose-200' : 'bg-rose-500/10 text-rose-300 border border-rose-500/20')}>
          {error}
        </div>
      )}

      {!readOnly && canWrite && adding && (
        <div className="mb-4">
          <Editor
            remark={addRemark} setRemark={setAddRemark}
            type={addType} setType={setAddType}
            at={addAt} setAt={setAddAt}
            onSave={handleAdd} onCancel={() => { setAdding(false); setAddRemark(''); }}
            saveLabel="Guardar nota"
            saving={saving} isLight={isLight}
          />
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8"><Loader2 size={20} className="animate-spin text-slate-400" /></div>
      ) : (notes && notes.length > 0) ? (
        <ul className="space-y-2.5">
          {notes.map(n => (
            <li key={n.id}>
              {editingId === n.id ? (
                <Editor
                  remark={editRemark} setRemark={setEditRemark}
                  type={editType} setType={setEditType}
                  at={editAt} setAt={setEditAt}
                  onSave={handleUpdate} onCancel={() => setEditingId(null)}
                  saveLabel="Guardar cambios"
                  saving={saving} isLight={isLight}
                />
              ) : (
                <div className={clsx('p-3.5 rounded-xl border', isLight ? 'bg-slate-50 border-slate-100' : 'bg-white/5 border-white/5')}>
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      {!onlyType && <TypeBadge t={n.note_type} isLight={isLight} />}
                      <span className={clsx('text-[11px] font-semibold', isLight ? 'text-slate-500' : 'text-slate-400')}>{fmtDateTime(n.note_at)}</span>
                    </div>
                    {canEditNote(n) && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => startEdit(n)} title="Editar" className={clsx('p-1.5 rounded-lg', isLight ? 'text-slate-500 hover:bg-slate-200' : 'text-slate-300 hover:bg-white/10')}>
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => handleDelete(n)} disabled={deletingId === n.id} title="Eliminar" className={clsx('p-1.5 rounded-lg disabled:opacity-50', isLight ? 'text-rose-500 hover:bg-rose-50' : 'text-rose-400 hover:bg-rose-500/10')}>
                          {deletingId === n.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        </button>
                      </div>
                    )}
                  </div>
                  <p className={clsx('text-sm leading-relaxed whitespace-pre-wrap break-words', isLight ? 'text-slate-800' : 'text-slate-100')}>{n.remark}</p>
                  {n.author?.full_name && (
                    <p className={clsx('text-[11px] mt-1.5', isLight ? 'text-slate-400' : 'text-slate-500')}>— {n.author.full_name}</p>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className={clsx('text-sm py-4 text-center', isLight ? 'text-slate-500' : 'text-slate-400')}>
          {readOnly ? 'Sin observaciones.' : 'Aún no hay notas para esta propiedad.'}
        </p>
      )}
    </div>
  );
};
